import axios from "axios";
import { toast } from "react-toastify";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { debounce } from "lodash";
import StatusChip from "../../Components/StatusChip";
import RejectionReasonModal from "../../Components/RejectionReasonModal";

function AdminAudioApproval() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [audios, setAudios] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const limit = 10;

  const downloadAudio = async (audioUrl, displayName) => {
    const safeUrl = audioUrl ? audioUrl.replace(/^http:\/\//i, 'https://') : audioUrl;
    try {
      const res = await fetch(safeUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = displayName || 'audio.mp3';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(safeUrl, '_blank');
    }
  };

  const getAllAudiosDetails = (searchString) => {
    const token = localStorage.getItem("token");
    axios
      .post(
        `${BASE_URL}admin/getAllAudiosDetails`,
        {
          page: currentPage,
          limit: limit,
          statusFilter: statusFilter,
          searchText: searchString ? searchString : searchText,
        },
        {
          headers: {
            "x-access-token": `${token}`,
            "Content-Type": "Application/json",
            version: "1.0.0",
          },
        }
      )
      .then((response) => {
        if (!response.data.success) {
          toast.error(response.data.message);
        } else {
          setAudios(response.data.data.data);
          setTotalPages(response.data.data.totalPages);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    const theme = localStorage.getItem("theme");
    document.body.className = theme;
    getAllAudiosDetails();
  }, [currentPage]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setCurrentPage(1);
    debouncedSearch(value);
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      getAllAudiosDetails(query);
    }, 500),
    []
  );

  var acceptReject = (audio_id, modifiedStatus) => {
    if (modifiedStatus === "rejected" || modifiedStatus === "failed") {
      setSelectedAudioId(audio_id);
      setSelectedStatus(modifiedStatus);
      setShowRejectionModal(true);
      return;
    }

    const token = localStorage.getItem("token");

    if (modifiedStatus === "approved") {
      const uniqueListId = window.prompt(
        "Please enter the unique list ID for this audio:"
      );

      if (!uniqueListId) {
        toast.error("Approval canceled: Unique list ID is required.");
        return;
      }

      axios
        .post(
          `${BASE_URL}admin/changeAudioStatus`,
          {
            audio_id: audio_id,
            modified_status: modifiedStatus,
            list_id: uniqueListId,
          },
          {
            headers: {
              "x-access-token": `${token}`,
              "Content-Type": "application/json",
              version: "1.0.0",
            },
          }
        )
        .then((response) => {
          setAudios((prevAudios) =>
            prevAudios.map((audio) => {
              if (audio.audio_id === audio_id) audio.status = modifiedStatus;
              return audio;
            })
          );
          toast.success(response.data.message);
          // Recalling API after edit
          getAllAudiosDetails();
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      // Handle other status changes (like pending)
      axios
        .post(
          `${BASE_URL}admin/changeAudioStatus`,
          {
            audio_id: audio_id,
            modified_status: modifiedStatus,
          },
          {
            headers: {
              "x-access-token": `${token}`,
              "Content-Type": "application/json",
              version: "1.0.0",
            },
          }
        )
        .then((response) => {
          setAudios((prevAudios) =>
            prevAudios.map((audio) => {
              if (audio.audio_id === audio_id) audio.status = modifiedStatus;
              return audio;
            })
          );
          toast.success(response.data.message);
          // Recalling API after edit
          getAllAudiosDetails();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handleRejectionSubmit = (reason) => {
    const token = localStorage.getItem("token");
    
    axios
      .post(
        `${BASE_URL}admin/changeAudioStatus`,
        {
          audio_id: selectedAudioId,
          modified_status: selectedStatus,
          reason: reason,
        },
        {
          headers: {
            "x-access-token": `${token}`,
            "Content-Type": "application/json",
            version: "1.0.0",
          },
        }
      )
      .then((response) => {
        setAudios((prevAudios) =>
          prevAudios.map((audio) => {
            if (audio.audio_id === selectedAudioId) {
              audio.status = selectedStatus;
              audio.reason = reason;
            }
            return audio;
          })
        );
        toast.success(response.data.message);
        // Recalling API after edit
        getAllAudiosDetails();
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to update audio status");
      });
  };

  const audioActions = (audio) => {
    const isMobile = window.innerWidth <= 576;
    return (
      <div>
        <div 
          className="d-flex gap-2" 
          style={{ 
            justifyContent: "center",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "4px" : "8px"
          }}
        >
          {audio.status !== "pending" && (
            <button
              title="Change status to pending"
              className="btn btn-outline-warning btn-sm d-flex align-items-center gap-1 shadow-sm"
              style={{ 
                padding: isMobile ? "4px 8px" : "6px 12px",
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                justifyContent: "center"
              }}
              onClick={() => acceptReject(audio.audio_id, "pending")}
            >
              <i className="fa-solid fa-circle-exclamation"></i>
              {isMobile && <span>Pending</span>}
            </button>
          )}
          {audio.status !== "approved" && (
            <button
              title="Approve"
              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1 shadow-sm"
              style={{ 
                padding: isMobile ? "4px 8px" : "6px 12px",
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                justifyContent: "center"
              }}
              onClick={() => acceptReject(audio.audio_id, "approved")}
            >
              <i className="fa-solid fa-check"></i>
              {isMobile && <span>Approve</span>}
            </button>
          )}
          {audio.status !== "rejected" && (
            <button
              title="Reject"
              className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 shadow-sm"
              style={{ 
                padding: isMobile ? "4px 8px" : "6px 12px",
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                justifyContent: "center"
              }}
              onClick={() => acceptReject(audio.audio_id, "rejected")}
            >
              <i className="fa-regular fa-circle-xmark"></i>
              {isMobile && <span>Reject</span>}
            </button>
          )}
          <button
            style={{ 
              height: isMobile ? "30px" : "40px", 
              borderRadius: "4px",
              padding: isMobile ? "4px 8px" : "6px 12px",
              fontSize: isMobile ? "0.75rem" : "0.875rem"
            }}
            className="btn btn-danger btn-sm d-flex align-items-center justify-content-center gap-1"
            onClick={() => deleteAudio(audio._id)}
          >
            <i class="bi bi-trash"></i>
            {isMobile && <span>Delete</span>}
          </button>
        </div>
      </div>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const preNextBtn = (displayText, compareToHide) => {
    return (
      <div
        style={{
          margin: window.innerWidth <= 576 ? "5px" : "10px",
          fontSize: window.innerWidth <= 576 ? "1rem" : "1.2rem",
          cursor: "pointer",
          display: currentPage == compareToHide ? "none" : "block",
        }}
        onClick={() => {
          console.log("here");
          handlePageChange(currentPage - (compareToHide == 1 ? 1 : -1));
        }}
      >
        {displayText}
      </div>
    );
  };

  const deleteAudio = (id) => {
    let confirm = window.confirm("Are you sure?");

    // console.log(confirm);
    if (confirm === false) {
      return;
    }

    const token = localStorage.getItem("token");
    axios
      .delete(`${BASE_URL}admin/deleteAudio/${id}`, {
        headers: {
          "Content-Type": "Application/json",
          version: "1.0.0",
          "x-access-token": `${token}`,
        },
      })
      .then((response) => {
        if (response.data.success == false) {
          toast.error(response.data.message);
        } else {
          toast.error(response.data.message);
          getAllAudiosDetails();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <div className="col-12 bst-seller pt-1" style={{ padding: window.innerWidth <= 576 ? "5px" : "10px" }}>
        <div className="card">
          <div className="card-header p-3">
            <h4 className="heading mb-0" style={{ fontSize: window.innerWidth <= 576 ? "1rem" : "1.25rem" }}>
              Audio List
            </h4>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              padding: window.innerWidth <= 576 ? "8px" : "4px",
              flexDirection: window.innerWidth <= 576 ? "column" : "row",
              gap: window.innerWidth <= 576 ? "8px" : "0",
            }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Search text here"
              value={searchText}
              style={{ 
                alignSelf: window.innerWidth <= 576 ? "stretch" : "flex-end", 
                maxWidth: window.innerWidth <= 576 ? "100%" : "300px" 
              }}
              onChange={handleSearch}
            />
          </div>

          <div className="card-body p-0">
            <div style={{ overflowX: "auto" }}>
              <table
                className="table table-bordered"
                style={{ 
                  textAlign: "center", 
                  tableLayout: window.innerWidth <= 768 ? "auto" : "fixed",
                  minWidth: window.innerWidth <= 768 ? "800px" : "100%"
                }}
              >
                <thead>
                  <tr>
                    <th style={{ 
                      verticalAlign: "top", 
                      width: window.innerWidth <= 768 ? "auto" : "5%",
                      fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                      padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                    }}>
                      Id
                    </th>
                    <th style={{ 
                      verticalAlign: "top", 
                      width: window.innerWidth <= 768 ? "auto" : "15%",
                      fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                      padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                    }}>
                      Name
                    </th>
                    <th style={{ 
                      verticalAlign: "top", 
                      width: window.innerWidth <= 768 ? "auto" : "20%",
                      fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                      padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                    }}>
                      Audio
                    </th>
                    <th style={{ 
                      verticalAlign: "top", 
                      width: window.innerWidth <= 768 ? "auto" : "20%",
                      fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                      padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                    }}>
                      <div>
                        Uploaded by <br />
                        <input
                          type="checkbox"
                          id="showuserdetailscheckbox"
                          className="form-check-input"
                          value="show details"
                          checked={showUserDetails}
                          onChange={() => {
                            setShowUserDetails(!showUserDetails);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="showuserdetailscheckbox"
                          style={{ fontSize: window.innerWidth <= 576 ? "0.75rem" : "0.875rem" }}
                        >
                          {window.innerWidth <= 576 ? "Details" : "Show user details"}
                        </label>
                      </div>
                    </th>
                    <th style={{ 
                      verticalAlign: "top", 
                      width: window.innerWidth <= 768 ? "auto" : "15%",
                      fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                      padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                    }}>
                      Uploaded at
                    </th>
                    <th style={{ 
                      verticalAlign: "top", 
                      width: window.innerWidth <= 768 ? "auto" : "10%",
                      fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                      padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                    }}>
                      Status / List Id
                    </th>
                    <th style={{ 
                      verticalAlign: "top", 
                      width: window.innerWidth <= 768 ? "auto" : "15%",
                      fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                      padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {audios.map((audio) => {
                    return (
                      <tr key={audio.audio_id}>
                        <td style={{ 
                          fontSize: window.innerWidth <= 576 ? "0.8rem" : "1rem",
                          padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                        }}>
                          {audio.audio_id}
                        </td>
                        <td
                          style={{
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                            fontSize: window.innerWidth <= 576 ? "0.8rem" : "1rem",
                            padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                          }}
                        >
                          {audio.audio_file_display_name}
                        </td>
                        <td style={{ padding: window.innerWidth <= 576 ? "8px 4px" : "8px" }}>
                          <div style={{ width: "100%", overflow: "hidden" }}>
                            <audio controls style={{ width: "100%", maxWidth: window.innerWidth <= 576 ? "200px" : "100%" }}>
                              <source src={audio.audio_file ? audio.audio_file.replace(/^http:\/\//i, 'https://') : ''} />
                              Your browser does not support the audio element.
                            </audio>
                            <button
                              className="btn btn-xs btn-outline-primary mt-1"
                              onClick={() => downloadAudio(audio.audio_file, audio.audio_file_display_name + '.mp3')}
                              title="Download audio"
                            >
                              <i className="fa fa-download"></i> Download
                            </button>
                          </div>
                        </td>
                        <td
                          style={{
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                            fontSize: window.innerWidth <= 576 ? "0.8rem" : "1rem",
                            padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                          }}
                        >
                          <div>
                            {audio.uploaded_by_user.name}
                            {showUserDetails && (
                              <>
                                <br />
                                {`(${audio.uploaded_by_user.email})`}
                                <br />
                                {`(${audio.uploaded_by_user.mobile_no})`}
                              </>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                            fontSize: window.innerWidth <= 576 ? "0.75rem" : "1rem",
                            padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                          }}
                        >
                          <div>
                            {moment(audio.upload_date).format(
                              window.innerWidth <= 576 ? "DD-MM-YY" : "DD-MM-YYYY hh:mm A"
                            )}
                          </div>
                        </td>
                        <td style={{ 
                          fontSize: window.innerWidth <= 576 ? "0.8rem" : "1rem",
                          padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                        }}>
                          {audio.status === "approved" ? (
                            audio.list_id
                          ) : (
                            <StatusChip status={audio.status || 'Approved'} size="sm" />
                          )}
                        </td>
                        <td
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: window.innerWidth <= 576 ? "8px 4px" : "8px"
                          }}
                        >
                          {audioActions(audio)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ 
                padding: window.innerWidth <= 576 ? "10px 5px" : "5px",
                flexDirection: window.innerWidth <= 576 ? "column" : "row",
                gap: window.innerWidth <= 576 ? "10px" : "5px"
              }}
            >
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: window.innerWidth <= 576 ? "center" : "flex-end"
              }}>
                {preNextBtn("<<", 1)}
                <div style={{ 
                  marginRight: window.innerWidth <= 576 ? "0" : "10px",
                  fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem"
                }}>
                  page {currentPage} of {totalPages}
                </div>
                {preNextBtn(">>", totalPages)}
              </div>
              <div>
                <select
                  className="form-select"
                  style={{ 
                    fontSize: window.innerWidth <= 576 ? "0.85rem" : "1rem",
                    padding: window.innerWidth <= 576 ? "4px 8px" : "6px 12px"
                  }}
                  onChange={(e) => handlePageChange(e.target.value)}
                >
                  <option disabled selected>
                    go to page
                  </option>
                  {Array(totalPages)
                    .fill()
                    .map((_, index) => index + 1)
                    .map((pageNo) => (
                      <option disabled={currentPage == pageNo} value={pageNo}>
                        {pageNo}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Reason Modal */}
      <RejectionReasonModal
        show={showRejectionModal}
        onHide={() => setShowRejectionModal(false)}
        onSubmit={handleRejectionSubmit}
        title={`Enter ${selectedStatus === 'rejected' ? 'Rejection' : 'Failure'} Reason`}
        submitText="Update Status"
        maxLength={300}
      />
    </>
  );
}
export default AdminAudioApproval;
